<template>
  <ui-modal-layout
    :title="tr('Customize Dashboard View')"
    :icon="LayoutDashboardIcon"
    stacked
    class="text-base"
  >
    <section class="flex flex-col gap-4">
      <div>
        <h3 class="m-0 text-sm font-semibold">
          {{ tr('Main Metrics') }}
        </h3>
        <p class="m-0 mt-1 text-sm text-muted-foreground">
          {{ tr('Select the three most important metrics to display on your dashboard') }}
        </p>
      </div>

      <div v-for="(label, index) in metricSlotLabels" :key="'metric-slot-' + index" class="grid gap-2">
        <ui-label :for="'dashboard-metric-' + index">
          {{ label }}
        </ui-label>
        <ui-combobox-select
          :id="'dashboard-metric-' + index"
          :model-value="metricValues[index]"
          :options="metricOptionsFor(index)"
          option-value="machine_name"
          :option-label="(option) => tr(option.label)"
          :search-placeholder="tr('Search') + '...'"
          :empty-text="tr('No results')"
          @update:model-value="(value) => metricValues[index] = value"
        />
      </div>
    </section>

    <ui-separator />

    <section class="flex flex-col gap-4">
      <div>
        <h3 class="m-0 text-sm font-semibold">
          {{ tr('Currency Settings') }}
        </h3>
        <p class="m-0 mt-1 text-sm text-muted-foreground">
          {{ tr('Choose how currency values are displayed in your dashboard.') }}
        </p>
      </div>

      <ui-combobox-select
        v-model="currencyValue"
        :options="currencyOptions"
        option-value="code"
        option-label="name"
        :placeholder="tr('Select currency')"
        :search-placeholder="tr('Search') + '...'"
        :empty-text="tr('No results')"
      />
    </section>

    <ui-separator />

    <section class="flex flex-col gap-5">
      <div>
        <h3 class="m-0 text-sm font-semibold">
          {{ tr('Estimated savings') }}
        </h3>
        <p class="m-0 mt-1 text-sm text-muted-foreground">
          {{ tr('Assumptions used to estimate how much money and time :app has saved by automatically stopping ads and handling tasks for you.') }}
        </p>
      </div>

      <div class="grid gap-2">
        <ui-label for="dashboard-reaction-days">
          {{ tr('Manual reaction time (days)') }}
        </ui-label>
        <ui-number-input
          id="dashboard-reaction-days"
          v-model="savingsReactionDays"
          :min="0"
          :max="30"
          :locale="$root.language"
          class="w-32"
        />
        <p class="m-0 text-xs text-muted-foreground">
          {{ tr('How long an underperforming, or expired-content and no-longer-valid ad would typically keep running before a human noticed and paused it.') }}
        </p>
      </div>

      <div class="grid gap-2">
        <ui-label for="dashboard-hourly-rate">
          {{ tr('Hourly labor rate') }}
        </ui-label>
        <div class="flex gap-2">
          <ui-number-input
            id="dashboard-hourly-rate"
            v-model="savingsHourlyRate"
            :min="0"
            :step="0.01"
            :locale="$root.language"
            class="flex-1"
          />
          <ui-combobox-select
            v-model="savingsHourlyRateCurrency"
            :options="currencies"
            option-value="code"
            option-label="name"
            :placeholder="tr('Select currency')"
            :search-placeholder="tr('Search') + '...'"
            :empty-text="tr('No results')"
            class="flex-1"
          />
        </div>
        <p class="m-0 text-xs text-muted-foreground">
          {{ tr('Used to convert the saved working time into a monetary value. If it differs from the dashboard currency, the rate is converted automatically.') }}
        </p>
      </div>

      <div class="grid gap-2">
        <ui-label>{{ tr('Minutes per automated action') }}</ui-label>
        <div
          v-for="action in minutesActions"
          :key="action.key"
          class="flex items-center justify-between gap-3"
        >
          <span class="text-sm">{{ tr(action.label) }}</span>
          <div class="flex items-center gap-2">
            <ui-number-input
              v-model="savingsMinutesPerAction[action.key]"
              :min="0"
              :max="240"
              :locale="$root.language"
              class="w-20"
            />
            <span class="w-8 text-sm text-muted-foreground">{{ tr('min') }}</span>
          </div>
        </div>
        <p class="m-0 text-xs text-muted-foreground">
          {{ tr('Approximate manual effort that each automated action replaces.') }}
        </p>
      </div>
    </section>

    <template #footer>
      <ui-button class="h-11 w-full" :disabled="isSaving" @click="() => saveSettings()">
        <loader-circle-icon v-if="isSaving" class="animate-spin" />
        {{ tr('Save') }}
      </ui-button>
    </template>
  </ui-modal-layout>
</template>

<script>
import { LayoutDashboard, LoaderCircle } from '@lucide/vue';
import { markRaw } from 'vue';

import { ESTIMATED_SAVINGS_ACTIONS } from '@/consts.js';
import DashboardService from '@/services/Dashboard';

/** Content of the dashboard "Customize View" modal: metrics, currency, savings assumptions. */
export default {
  name: 'DashboardSettingsDialog',
  components: {
    LoaderCircleIcon: LoaderCircle,
  },
  props: {
    onSettingsUpdated: {
      type: Function,
      required: false,
      default: () => null,
    },
  },
  data() {
    const savingsMinutesPerAction = {};
    ESTIMATED_SAVINGS_ACTIONS.forEach(action => savingsMinutesPerAction[action.key] = 5);

    return {
      LayoutDashboardIcon: markRaw(LayoutDashboard),
      isSaving: false,
      originalSettings: null,
      metricValues: [],
      currencyValue: null,
      savingsReactionDays: 3,
      savingsHourlyRate: 10000,
      savingsHourlyRateCurrency: null,
      savingsMinutesPerAction,
      minutesActions: ESTIMATED_SAVINGS_ACTIONS,
      metricSlotLabels: [ this.tr('Primary Metric'), this.tr('Secondary Metric'), this.tr('Tertiary Metric') ],
    };
  },
  computed: {
    apiPayload() {
      return {
        metrics: this.metricValues,
        currency: this.currencyValue,
        savings_estimate: {
          reaction_days: this.savingsReactionDays,
          hourly_rate: this.savingsHourlyRate,
          hourly_rate_currency: this.savingsHourlyRateCurrency,
          minutes_per_action: this.savingsMinutesPerAction,
        },
      };
    },
    hasChanges() {
      return JSON.stringify(this.apiPayload) !== this.originalSettings;
    },
    metricOptions() {
      return this.$store.getters['app/dashboardMetrics'];
    },
    currencies() {
      return this.$store.getters['app/currencies'];
    },
    currencyOptions() {
      return [
        { code: 'auto', name: 'Auto' },
        ...this.currencies,
      ];
    },
  },
  created() {
    this.metricValues = this.$root.loggedInUser.dashboard_settings.metrics.map(m => m.machine_name);
    this.currencyValue = this.$root.loggedInUser.dashboard_settings.currency;

    const savings = this.$root.loggedInUser.dashboard_settings.savings_estimate;
    if (savings) {
      this.savingsReactionDays = savings.reaction_days ?? this.savingsReactionDays;
      this.savingsHourlyRate = savings.hourly_rate ?? this.savingsHourlyRate;
      this.savingsHourlyRateCurrency = savings.hourly_rate_currency ?? null;
      if (savings.minutes_per_action) {
        this.savingsMinutesPerAction = {
          ...this.savingsMinutesPerAction,
          ...savings.minutes_per_action,
        };
      }
    }

    this.originalSettings = JSON.stringify(this.apiPayload);
  },
  methods: {
    // A metric picked in another slot is not offered again.
    metricOptionsFor(index) {
      const takenElsewhere = this.metricValues.filter((value, i) => i !== index);

      return this.metricOptions.filter(option => !takenElsewhere.includes(option.machine_name));
    },
    saveSettings() {
      if (this.isSaving) {
        return;
      }

      this.$toast.clear();

      if (!this.hasChanges) {
        this.$fullscreenModal.closeAll();
        return;
      }

      this.isSaving = true;

      DashboardService.updateSettings(this.apiPayload)
        .then(resp => {
          this.$root.loggedInUser.dashboard_settings = resp;

          this.originalSettings = JSON.stringify(this.apiPayload);
          this.$toast.success(this.tr('Dashboard settings saved successfully.'));
          this.onSettingsUpdated();
          this.$fullscreenModal.closeAll();
          return resp;
        })
        .catch(() => {
          this.$toast.error(this.tr('Failed to save settings.'));
        })
        .finally(() => this.isSaving = false);
    },
  },
};
</script>
